import { array, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { type IconName } from '@databox/icons/dist/types';

import type { Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isElementScrollable } from 'webapp/utils/html';

import eq from 'webapp/helpers/eq';
import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import OnboardingCommonBottomComponent from 'webapp/components/onboarding/common/bottom';
import OnboardingCommonTopComponent from 'webapp/components/onboarding/common/top';

import { EXPIRE_WARNING } from 'webapp/models-custom/infolayer-item/base';
import type InfolayerItemGeneral from 'webapp/models-custom/infolayer-item/general';

type Args = Record<string, never>;

export enum AccountType {
	COMPANY = 'company',
	AGENCY = 'agency',
}

export enum CompanySize {
	'1-10' = '1-10',
	'11-50' = '11-50',
	'51-250' = '51-250',
	'251-1K' = '251-1K',
	'1K-5K' = '1K-5K',
}

export default class OnboardingStepsAccountSetupComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('experiment/genie-onboarding-920261')
	declare experimentGenieOnboarding920261Service: Service.Experiment.GenieOnboarding920261;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('onboarding') declare onboardingService: Service.Onboarding;

	@tracked selectedAccountType: AccountType | null = null;
	@tracked selectedCompanySize: CompanySize | null = null;

	node: HTMLDivElement | null = null;
	infoLayer: InfolayerItemGeneral | null = null;

	accountTypes: {
		value: AccountType;
		title: string;
		description: string;
		iconName: IconName;
		testId: string;
	}[] = [
		{
			value: AccountType.COMPANY,
			title: 'Company or individual',
			description: 'I want to track performance for my company, team, or just myself.',
			iconName: 'Company',
			testId: 'test-selectAccountTypeCompany',
		},
		{
			value: AccountType.AGENCY,
			title: 'Organization',
			description:
				'I’m tracking performance across multiple <strong>clients, brands, locations, or business units</strong>.',
			iconName: 'Clients',
			testId: 'test-selectAccountTypeAgency',
		},
	];

	companySizes: { value: CompanySize; label: string }[] = [
		{
			value: CompanySize['1-10'],
			label: '1 - 10',
		},
		{
			value: CompanySize['11-50'],
			label: '11 - 50',
		},
		{
			value: CompanySize['51-250'],
			label: '51 - 250',
		},
		{
			value: CompanySize['251-1K'],
			label: '251 - 1,000',
		},
		{
			value: CompanySize['1K-5K'],
			label: '1,001+',
		},
	];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (this.onboardingService.templateId) {
			this.analyticsService.event(TrackingEvent.ONBOARDING_TEMPLATE);
		} else {
			this.analyticsService.event(TrackingEvent.ONBOARDING_START, {
				experiments: [this.experimentGenieOnboarding920261Service.experimentOrigin],
			});

			this.analyticsService.properties(
				{ experiments: this.experimentGenieOnboarding920261Service.experimentOrigin },
				['user', 'company'],
			);
		}
	}

	get title(): string {
		const firstName = this.currentUserService?.model?.firstName;
		return `Hey${firstName ? `, ${firstName}` : ''}. Let's get you set up.`;
	}

	get isContinueBtnDisabled(): boolean {
		return !this.selectedAccountType || !this.selectedCompanySize;
	}

	@action
	selectAccountType(value: AccountType): void {
		this.selectedAccountType = value;

		this.scrollToCompanySizesSection();
	}

	@action
	selectCompanySize(value: CompanySize): void {
		this.selectedCompanySize = value;
	}

	@action
	continue(): void {
		if (this.isContinueBtnDisabled) {
			if (!this.infoLayer) {
				this.infoLayer = this.infolayerService.warning('Please select profile and size');

				setTimeout(() => {
					this.infoLayer = null;
				}, EXPIRE_WARNING);
			}

			return;
		}

		if (this.selectedAccountType === AccountType.AGENCY) {
			void this.accountService.saveSpaceAsAgency();
		}

		void this.accountService.saveMetadataProperties({
			employees: this.selectedCompanySize,
		});

		this.analyticsService.event(TrackingEvent.ONBOARDING_METADATA_COMPLETED);

		this.onboardingService.nextStep();
	}

	@action
	setupView(node: HTMLElement) {
		this.node = node as HTMLDivElement;
	}

	private scrollToCompanySizesSection(): void {
		if (!this.node) {
			return;
		}

		if (!isElementScrollable(this.node)) {
			return;
		}
		this.node.querySelector('.company-sizes-section')?.scrollIntoView({ behavior: 'smooth' });
	}
	<template>
		<OnboardingCommonTopComponent
			@title={{this.title}}
			@subtitle="Tell us a bit about yourself so we can customize your experience."
		/>

		<div class="onboarding-main smart-center-pos" {{didInsert this.setupView}}>
			<div class="container">
				<section class="account-types-section">
					<h3>First things first — who are you tracking performance for?</h3>
					<div class="account-types">
						{{#each this.accountTypes as |accountType|}}
							<div
								role="button"
								class={{joinClasses
									(array
										"account-type"
										(if
											(eq this.selectedAccountType accountType.value)
											"account-type--selected"
										)
									)
								}}
								data-testid={{accountType.testId}}
								{{on "click" (fn this.selectAccountType accountType.value)}}
							>
								<div class="account-type__icon">
									<CommonUiIconComponent
										@name={{accountType.iconName}}
										@size="xl"
									/>
								</div>
								<div class="account-type__content">
									<span>{{accountType.title}}</span>
									<p>{{{accountType.description}}}</p>
								</div>
							</div>
						{{/each}}
					</div>
				</section>
				<section class="company-sizes-section">
					<h3>How many people work at your company?</h3>
					<div class="company-sizes">
						{{#each this.companySizes as |companySize|}}
							<div
								role="button"
								class={{joinClasses
									(array
										"company-size"
										(if
											(eq this.selectedCompanySize companySize.value)
											"company-size--selected"
										)
									)
								}}
								{{on "click" (fn this.selectCompanySize companySize.value)}}
							>
								<span>{{companySize.label}}</span>
							</div>
						{{/each}}
					</div>
				</section>
			</div>
		</div>

		<OnboardingCommonBottomComponent
			@skipAction={{null}}
			@continueAction={{this.continue}}
			@isButtonDisabled={{this.isContinueBtnDisabled}}
		/>
	</template>
}
